// Rótulos e formatadores do financeiro (nunca mostrar o código cru).
import { formatarCentavos } from '@/pages/loja/utils';

export type TipoChip = 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'neutro';

// Centavos inteiros → "R$ 1.234,56". Campo ausente → "—" (nunca R$ 0,00 falso).
export function reais(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return formatarCentavos(n);
}

// Contagem: ausente → "—".
export function conta(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '—';
}

// "1 pagamento" / "2 pagamentos": o singular nunca fica no plural.
export function plural(v: number | null | undefined, um: string, varios: string): string {
  return Number(v) === 1 ? um : varios;
}

const FUSO = 'America/Sao_Paulo';

// Horário de Brasília "HH:MM".
export function horaBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });
}

// "dd/mm/aaaa HH:MM" (hora de Brasília) — para estornos e repasses.
export function dataHoraBR(iso: string | null | undefined): string {
  const data = dataBR(iso);
  if (!data) return '';
  return `${data} ${horaBR(iso)}`;
}

// "dd/mm/aaaa" (hora local).
export function dataBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// "20/10" quando é o ano atual; "20/10/2025" quando é de outro ano.
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  if (d.getFullYear() === new Date().getFullYear()) return `${dia}/${mes}`;
  return `${dia}/${mes}/${d.getFullYear()}`;
}

// Forma de pagamento como a pessoa lê ("pix" → "Pix").
export function rotuloForma(forma: string | null | undefined): string {
  if (!forma) return '';
  const f = forma.trim();
  if (!f) return '';
  return f.charAt(0).toUpperCase() + f.slice(1);
}

// Nome do evento → pedaço seguro para nome de arquivo.
export function slugificar(nome: string): string {
  const s = nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return s || 'evento';
}

export const ROTULO_ORIGEM: Record<string, string> = {
  ingresso_inscricao: 'Ingressos e inscrições pelo site',
  lancado_a_mao: 'Lançados à mão (pagos direto para vocês)',
  fichas_online: 'Fichas pelo site',
  caixa_maquininha: 'Caixa · maquininha',
  caixa_dinheiro: 'Caixa · dinheiro',
  rifa_pix: 'Ação entre amigos · Pix',
  rifa_na_mao: 'Ação entre amigos · na mão',
};

export function rotuloOrigem(o: string | null | undefined): string {
  if (!o) return 'Outros';
  return ROTULO_ORIGEM[o] ?? o;
}

export const CHIP_ONDE: Record<string, { rotulo: string; tipo: TipoChip }> = {
  organizador: { rotulo: 'na sua conta do Mercado Pago', tipo: 'success' },
  seliga: { rotulo: 'com o Se Liga', tipo: 'info' },
  fora: { rotulo: 'com vocês', tipo: 'secondary' },
};

export function chipOnde(onde: string | null | undefined): { rotulo: string; tipo: TipoChip } {
  if (!onde) return { rotulo: 'com vocês', tipo: 'secondary' };
  return CHIP_ONDE[onde] ?? { rotulo: onde, tipo: 'neutro' };
}

export const ROTULO_ESTORNO: Record<string, string> = {
  inscricao: 'Ficha / ingresso',
  caixa_desfeita: 'Venda do caixa desfeita',
  loja_estorno: 'Estorno da loja',
};

export function rotuloEstorno(o: string | null | undefined): string {
  if (!o) return 'Estorno';
  return ROTULO_ESTORNO[o] ?? o;
}

const ROTULO_SENTIDO: Record<string, string> = {
  para_organizador: 'Se Liga → vocês',
  para_seliga: 'vocês → Se Liga',
};

export function rotuloSentido(sentido: string | null | undefined): string {
  if (!sentido) return 'Repasse';
  return ROTULO_SENTIDO[sentido] ?? sentido;
}

// Frase da faixa de situação do dinheiro.
export function textoSituacao(
  situacao: string | null | undefined,
  saldo: number | null | undefined,
): string {
  if (situacao === 'repassar') return `O Se Liga vai repassar ${reais(saldo)}`;
  if (situacao === 'cobrar') {
    const v = saldo === null || saldo === undefined ? null : Math.abs(Number(saldo));
    return `Vocês devem ${reais(v)} ao Se Liga`;
  }
  if (situacao === 'em_dia') return 'Em dia ✓';
  return '';
}

// Cores da faixa de situação (só tokens do StyleSystem).
export function corSituacao(situacao: string | null | undefined): string {
  if (situacao === 'repassar') return 'bg-warning-100 text-warning-800';
  if (situacao === 'cobrar') return 'bg-danger-100 text-danger-800';
  if (situacao === 'em_dia') return 'bg-success-100 text-success-800';
  return 'bg-secondary-100 text-secondary-700';
}